/*
  Automatic free gift.

  Keeps exactly one gift line in the cart whenever the cart contains anything
  the customer actually chose, and removes it when the cart empties.

  Qualification used to require a _free_gift_qualifier property, which only the
  product-page Free gift block wrote. That meant templates without the block —
  and collection quick-add — never triggered the gift. Now any non-gift line
  qualifies, so it works cart-wide no matter how the item was added.

  Driven by [data-free-gift-config][data-gift-variant-id], emitted site-wide by
  snippets/free-gift-auto.liquid.
*/
(() => {
  'use strict';

  if (window.FreeGiftIncluded) return;

  const configEl = document.querySelector('[data-free-gift-config]');
  if (!configEl) return;

  const giftVariantId = Number(configEl.dataset.giftVariantId);
  if (!giftVariantId) return;

  /* Theme settings is the master switch. Four different elements can carry this
     config — the product block's card, the standalone section's card, cart line
     items, and the site-wide emitter — and querySelector takes whichever comes
     first in the DOM. They all publish the same toggle and scope so behaviour
     cannot change depending on which one happens to win. */
  if (String(configEl.dataset.giftAutoAdd || '') !== 'true') return;

  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const routeTo = (path) => `${root}${path}`.replace(/\/{2,}/g, '/');

  const request = async (path, options = {}) => {
    const response = await fetch(routeTo(path), {
      credentials: 'same-origin',
      headers: { Accept: 'application/json', ...(options.headers || {}) },
      ...options,
    });
    if (!response.ok) throw new Error(`Free gift cart request failed (${response.status})`);
    return response.json();
  };

  const setLineQuantity = (key, quantity) =>
    request('cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: key, quantity }),
    });

  const addGift = () =>
    request('cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        items: [{ id: giftVariantId, quantity: 1, properties: { _free_gift: 'true' } }],
      }),
    });

  /* Only fired when something actually changed, so this cannot loop:
     the follow-up sync finds the cart already correct and stays quiet. */
  const notifyTheme = () =>
    document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { open: true } }));

  const isGiftLine = (item) =>
    Number(item.variant_id) === giftVariantId && String((item.properties || {})._free_gift || '') === 'true';

  /* Which product types earn the gift. Matched case-insensitively because the
     catalogue is inconsistent: "f1 racing suit" is lowercase while
     "Karting Suit" is title case. Empty list means every product qualifies. */
  const allowedTypes = new Set(
    String(configEl.dataset.giftProductTypes || '')
      .split(',')
      .map((type) => type.trim().toLowerCase())
      .filter(Boolean)
  );

  const qualifies = (item) => {
    if (Number(item.variant_id) === giftVariantId) return false;
    if (allowedTypes.size === 0) return true;
    return allowedTypes.has(String(item.product_type || '').trim().toLowerCase());
  };

  const sync = async () => {
    const cart = await request('cart.js');

    /* The gift itself never qualifies, so a gift line left in the cart on its
       own cannot keep itself alive. */
    const purchasedItems = cart.items.filter(qualifies);
    const giftLines = cart.items.filter(isGiftLine);

    let changed = false;

    if (purchasedItems.length === 0) {
      for (const line of giftLines) {
        await setLineQuantity(line.key, 0);
        changed = true;
      }
    } else if (giftLines.length === 0) {
      await addGift();
      changed = true;
    } else {
      /* Keep the first gift line at 1 and clear any duplicates. */
      for (let i = 0; i < giftLines.length; i++) {
        const wanted = i === 0 ? 1 : 0;
        if (giftLines[i].quantity !== wanted) {
          await setLineQuantity(giftLines[i].key, wanted);
          changed = true;
        }
      }
    }

    if (changed) notifyTheme();
  };

  /* Serialise: cart writes race badly if two syncs overlap. */
  let inFlight = null;
  const queueSync = () => {
    if (!inFlight) {
      inFlight = sync()
        .catch((error) => console.warn('[Free gift]', error))
        .finally(() => {
          inFlight = null;
        });
    }
    return inFlight;
  };

  window.FreeGiftIncluded = { sync: queueSync };

  document.addEventListener('product-ajax:added', queueSync);
  document.addEventListener('cart:updated', queueSync);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', queueSync, { once: true });
  } else {
    queueSync();
  }
})();
